import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useGetInstructionQuery } from '@/features/instructions/instructionsApi'
import { ArticleModerationPoller } from '@/features/moderation/ArticleModerationPoller'
import { EditorFetchFailed } from './EditorFetchFailed'
import { ArticleEditorBody } from './ArticleEditorBody'
import type { EditorTabId } from './EditorRail'
import { ArticleLoadGate } from './ArticleLoadGate'
import { useArticleSeed } from './useArticleSeed'
import { useSeedAcrossCreate } from './useSeedAcrossCreate'
import { useEditLang } from './useEditLang'

const isNotFound = (error: unknown): boolean =>
  !!error && typeof error === 'object' && 'status' in error && error.status === 404

export const InstructionEditor = () => {
  const { slug } = useParams<{ slug?: string }>()
  // Вкладка и язык живут здесь, а не в теле: после создания тело уходит под гейт загрузки
  // (засева серверной статьи ещё нет) и размонтируется, а этот компонент — нет: обе ветки
  // маршрута рендерят его на той же позиции.
  const [tab, setTab] = useState<EditorTabId>('content')
  // `currentData`, а не `data`: на смене slug `data` ещё держит прошлую статью.
  const {
    currentData: instruction,
    error,
    isError,
    refetch,
  } = useGetInstructionQuery(slug ?? '', { skip: !slug })
  const notFound = !!slug && !instruction && isNotFound(error)
  const { seed, seedReadAt, loadFailed, retry } = useArticleSeed({
    slug,
    instruction,
    instructionFailed: !!slug && isError && !instruction && !notFound,
    refetchInstruction: refetch,
  })

  const shownSeed = useSeedAcrossCreate(slug, seed)

  const [lang, setLang] = useEditLang(slug, instruction)

  if (notFound) return <EditorFetchFailed />

  // Поллер — на постоянной позиции: смена гейт ⇄ форма его не перемонтирует.
  return (
    <>
      {instruction?.id ? <ArticleModerationPoller id={instruction.id} /> : null}
      {shownSeed ? (
        <ArticleEditorBody
          instruction={instruction}
          seed={shownSeed}
          seedReadAt={seedReadAt}
          isCreate={!slug}
          tab={tab}
          onTabChange={setTab}
          lang={lang}
          onLangChange={setLang}
        />
      ) : (
        <ArticleLoadGate isError={loadFailed} onRetry={retry} />
      )}
    </>
  )
}
